/**
 * Client helpers for SOP authoring drafts (schema_version 1).
 */

import { ApiError, apiFetch } from '@/lib/api';
import type { SopListItem, SopStepRow } from '@/lib/sops-admin';
import {
  authoringStepsToFlowSteps,
  newTemporaryDraftStepId,
  normalizeAuthoringDocument,
  publishedStepDraftId,
  type SopAuthoringDocument,
  type SopAuthoringStep,
} from '../../shared/lib/sop-authoring-document.domain.js';
import { isMultipleChoiceStep, parseMultipleChoiceConfig } from '../../shared/lib/multiple-choice-step.domain.js';
import type {
  NarrativeBranchDisposition,
  NarrativeStepProjection,
} from '../../shared/lib/sop-narrative-projection.domain.js';

export type NarrativeReviewApiResponse = {
  sop_id: number;
  policy_version: number;
  aggregate_fingerprint: string;
  has_baseline: boolean;
  baseline_policy_version: number | null;
  baseline_fingerprint: string | null;
  legacy_bulk_accept_eligible: boolean;
  draft_revision: number;
  base_published_revision: number;
  steps: NarrativeStepProjection[];
  pending: Array<{
    draft_step_id: string;
    branch_key: string;
    reason: 'unreviewed' | 'changed' | 'incomplete' | 'policy_changed';
  }>;
  reviewed_by_step: Record<
    string,
    { fingerprint: string; dispositions: Record<string, NarrativeBranchDisposition> }
  >;
  provisional_dispositions: Record<string, Record<string, NarrativeBranchDisposition>> | null;
};

export type SopRevisionImpactPolicy = 'CONTINUE' | 'PROMPT' | 'REQUIRE_RESTART';

export type SopAuthoringRevisionImpactPreflight = {
  sop_id: number;
  materiality: 'non_material' | 'material' | 'uncertain';
  needs_decision: boolean;
  can_decide: boolean;
  blocked_message: string | null;
  active_attempt_count: number;
  configuration_revision: string;
  draft_revision: number;
  base_published_revision: number;
  published_revision: number;
};

export type SopRevisionImpactDecision = {
  decisionKey: string;
  policy: SopRevisionImpactPolicy;
  reason?: string | null;
};

export type PublishAuthoringDraftApiResult = {
  sop_id: number;
  published_revision: number;
  is_active: boolean;
  step_id_map: Record<string, number>;
  revision_impact?: {
    event_id: string;
    policy: SopRevisionImpactPolicy;
    affected_attempt_count: number;
    idempotent: boolean;
  } | null;
};

export type AuthoringDraftApiResponse = {
  sop_id: number;
  base_published_revision: number;
  draft_revision: number;
  document: SopAuthoringDocument;
  pending_review_count?: number;
  created_by?: string | null;
  updated_by?: string | null;
  created_at?: string;
  updated_at?: string;
};

/** Stable negative local id for tmp: draft steps (UI / DnD only). */
export function localIdForDraftStepId(draftStepId: string): number {
  const published = draftStepId.match(/^step:(\d+)$/);
  if (published) return Number(published[1]);
  let hash = 0;
  for (let i = 0; i < draftStepId.length; i += 1) {
    hash = (hash * 31 + draftStepId.charCodeAt(i)) | 0;
  }
  const abs = Math.abs(hash) || 1;
  return -abs;
}

export function draftStepIdForLocalStep(step: SopStepRow & { draft_step_id?: string }): string {
  if (step.draft_step_id) return step.draft_step_id;
  if (Number(step.id) > 0) return publishedStepDraftId(Number(step.id));
  return newTemporaryDraftStepId();
}

function remapLinkToDraftId(
  value: string | number | null | undefined,
  idToDraft: Map<number, string>,
): string | null {
  if (value == null || value === '') return null;
  const text = String(value).trim();
  if (text.startsWith('step:') || text.startsWith('tmp:')) return text;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  const mapped = idToDraft.get(numeric);
  if (mapped) return mapped;
  if (numeric > 0) return publishedStepDraftId(numeric);
  throw new Error(`Unknown local link target "${numeric}"`);
}

export function stepsAndSopToAuthoringDocument(
  sop: SopListItem,
  steps: Array<SopStepRow & { draft_step_id?: string }>,
): SopAuthoringDocument {
  const idToDraft = new Map<number, string>();
  for (const step of steps) {
    const draftId = draftStepIdForLocalStep(step);
    idToDraft.set(Number(step.id), draftId);
  }

  const authoringSteps: SopAuthoringStep[] = steps.map((step, index) => {
    const draftStepId = idToDraft.get(Number(step.id))!;
    const publishedStepId = Number(step.id) > 0 ? Number(step.id) : null;
    let config = step.config ?? null;
    if (isMultipleChoiceStep(step.archetype)) {
      const parsed = parseMultipleChoiceConfig(config);
      const rawOptions =
        config != null && typeof config === 'object' && Array.isArray((config as any).options)
          ? ((config as any).options as Array<Record<string, unknown>>)
          : parsed.options;
      config = {
        options: rawOptions.map((option: any, optionIndex: number) => ({
          id: String(option.id ?? parsed.options[optionIndex]?.id ?? `option_${optionIndex + 1}`),
          label: String(option.label ?? ''),
          report_output: String(option.report_output ?? option.reportText ?? ''),
          next_step_id: remapLinkToDraftId(
            option.next_step_id ?? option.next_step,
            idToDraft,
          ),
        })),
      };
    }
    return {
      draft_step_id: draftStepId,
      published_step_id: publishedStepId,
      position: Number(step.step_order ?? index + 1) || index + 1,
      title: String(step.title ?? '').trim() || 'Untitled step',
      archetype: step.archetype ?? null,
      variant: step.variant ?? null,
      prompt_text: step.prompt_text ?? null,
      camera_ref: step.camera_ref ?? null,
      url: step.url ?? null,
      config,
      contact_reference_type: step.contact_reference_type ?? null,
      contact_reference_value: step.contact_reference_value ?? null,
      contact_reference_scope: step.contact_reference_scope ?? null,
      url_reference_type: step.url_reference_type ?? null,
      url_reference_value: step.url_reference_value ?? null,
      choice_positive_label: step.choice_positive_label ?? null,
      choice_negative_label: step.choice_negative_label ?? null,
      positive_response_format: step.positive_response_format ?? null,
      negative_response_format: step.negative_response_format ?? null,
      include_negative_in_report: Boolean(step.include_negative_in_report),
      include_in_report: step.include_in_report === undefined ? true : Boolean(step.include_in_report),
      show_in_narrative_strip: Boolean(step.show_in_narrative_strip),
      report_action_text: step.report_action_text ?? null,
      is_active: step.is_active === undefined ? true : Boolean(step.is_active),
      next_step: remapLinkToDraftId(step.next_step, idToDraft),
      yes_next_step: remapLinkToDraftId(step.yes_next_step, idToDraft),
      no_next_step: remapLinkToDraftId(step.no_next_step, idToDraft),
    };
  });

  return normalizeAuthoringDocument({
    schema_version: 1,
    sop: {
      name: sop.name || 'Untitled SOP',
      description: sop.description ?? null,
      scope_type: (sop.scope_type || sop.owner_type || 'GLOBAL') as 'GLOBAL' | 'DEALER' | 'SITE',
      scope_id: sop.scope_id ?? sop.owner_id ?? null,
      is_inheritable: Boolean(sop.is_inheritable),
      is_hidden_from_descendants: Boolean(sop.is_hidden_from_descendants),
    },
    steps: authoringSteps,
  });
}

export function authoringDocumentToLocalSteps(
  document: SopAuthoringDocument,
  sopId: number,
): Array<SopStepRow & { draft_step_id: string }> {
  const draftToLocal = new Map<string, number>();
  for (const step of document.steps) {
    draftToLocal.set(step.draft_step_id, localIdForDraftStepId(step.draft_step_id));
  }
  const remap = (draftId: string | null): number | null => {
    if (!draftId) return null;
    return draftToLocal.get(draftId) ?? null;
  };

  return document.steps.map((step) => {
    let config = step.config;
    if (isMultipleChoiceStep(step.archetype)) {
      const options = Array.isArray((step.config as any)?.options) ? (step.config as any).options : [];
      config = {
        options: options.map((option: any) => ({
          id: option.id,
          label: option.label,
          report_output: option.report_output ?? '',
          next_step_id: remap(option.next_step_id == null ? null : String(option.next_step_id)),
        })),
      };
    }
    return {
      id: localIdForDraftStepId(step.draft_step_id),
      draft_step_id: step.draft_step_id,
      sop_id: sopId,
      step_order: step.position,
      title: step.title,
      archetype: step.archetype,
      variant: step.variant,
      prompt_text: step.prompt_text,
      camera_ref: step.camera_ref,
      url: step.url,
      config,
      contact_reference_type: step.contact_reference_type,
      contact_reference_value: step.contact_reference_value,
      contact_reference_scope: step.contact_reference_scope,
      url_reference_type: step.url_reference_type,
      url_reference_value: step.url_reference_value,
      choice_positive_label: step.choice_positive_label,
      choice_negative_label: step.choice_negative_label,
      positive_response_format: step.positive_response_format,
      negative_response_format: step.negative_response_format,
      include_negative_in_report: step.include_negative_in_report,
      include_in_report: step.include_in_report,
      show_in_narrative_strip: step.show_in_narrative_strip,
      report_action_text: step.report_action_text,
      is_active: step.is_active,
      next_step: remap(step.next_step),
      yes_next_step: remap(step.yes_next_step),
      no_next_step: remap(step.no_next_step),
    };
  });
}

export type SopDraftFlowProjection = {
  steps: SopStepRow[];
  localIdToValidationId: Map<number, number>;
  validationIdToLocalId: Map<number, number>;
};

export type SopEditorBootstrap =
  | { source: 'draft'; draft: AuthoringDraftApiResponse }
  | { source: 'published'; steps: SopStepRow[] };

/**
 * Project local draft rows onto positive validation IDs without changing the
 * local IDs used by React/DnD. The shared flow validator intentionally rejects
 * non-positive IDs, while unsaved `tmp:` steps use stable negative local IDs.
 */
export function projectSopDraftStepsForFlowHealth(steps: SopStepRow[]): SopDraftFlowProjection | null {
  if (!steps.some((step) => Number(step.id) <= 0 || step.draft_step_id?.startsWith('tmp:'))) {
    return null;
  }

  const rowsWithDraftIds = steps.map((step) => ({
    ...step,
    draft_step_id: draftStepIdForLocalStep(step),
  }));
  const document = stepsAndSopToAuthoringDocument(
    { id: 0, name: 'Flow validation' },
    rowsWithDraftIds,
  );
  const projectedSteps = authoringStepsToFlowSteps(document.steps) as SopStepRow[];
  const localIdToValidationId = new Map<number, number>();
  const validationIdToLocalId = new Map<number, number>();

  const localByDraftId = new Map(rowsWithDraftIds.map((step) => [step.draft_step_id, step]));
  document.steps.forEach((authoringStep, index) => {
    const projectedStep = projectedSteps[index];
    const localStep = localByDraftId.get(authoringStep.draft_step_id);
    if (!projectedStep || !localStep) return;
    localIdToValidationId.set(Number(localStep.id), Number(projectedStep.id));
    validationIdToLocalId.set(Number(projectedStep.id), Number(localStep.id));
  });

  return { steps: projectedSteps, localIdToValidationId, validationIdToLocalId };
}

export async function ensureAuthoringDraft(sopId: number): Promise<AuthoringDraftApiResponse> {
  return apiFetch<AuthoringDraftApiResponse>(`/api/sops/${sopId}/authoring-draft`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

/** Read an existing draft without creating one when an editor is merely opened. */
export async function fetchAuthoringDraft(sopId: number): Promise<AuthoringDraftApiResponse> {
  return apiFetch<AuthoringDraftApiResponse>(`/api/sops/${sopId}/authoring-draft`);
}

export async function fetchPublishedSopSteps(sopId: number): Promise<SopStepRow[]> {
  const response = await apiFetch<{ ok?: boolean; data?: SopStepRow[] }>(`/api/sops/${sopId}/steps`);
  return Array.isArray(response?.data) ? response.data : [];
}

/**
 * Load the source appropriate for the editor surface. Editable SOPs prefer an
 * existing draft, while read-only surfaces never create or read authoring drafts.
 */
export async function fetchSopEditorBootstrap(
  sopId: number,
  editable: boolean,
): Promise<SopEditorBootstrap> {
  if (editable) {
    try {
      return { source: 'draft', draft: await fetchAuthoringDraft(sopId) };
    } catch (err) {
      if (!(err instanceof ApiError) || err.status !== 404) throw err;
    }
  }
  return { source: 'published', steps: await fetchPublishedSopSteps(sopId) };
}

/** Serialize asynchronous full-document mutations while preserving failures. */
export function createSerializedAsyncQueue() {
  let tail: Promise<void> = Promise.resolve();
  return function enqueue<T>(operation: () => Promise<T>): Promise<T> {
    const next = tail.then(operation, operation);
    tail = next.then(() => undefined, () => undefined);
    return next;
  };
}

export async function putAuthoringDraft(
  sopId: number,
  document: SopAuthoringDocument,
  expectedDraftRevision: number,
): Promise<AuthoringDraftApiResponse> {
  return apiFetch<AuthoringDraftApiResponse>(`/api/sops/${sopId}/authoring-draft`, {
    method: 'PUT',
    body: JSON.stringify({ document, expectedDraftRevision }),
  });
}

export async function fetchNarrativeReview(sopId: number): Promise<NarrativeReviewApiResponse> {
  return apiFetch<NarrativeReviewApiResponse>(`/api/sops/${sopId}/authoring-draft/narrative-review`);
}

/**
 * Preflight the saved draft against in-progress reports. Read-only: the draft and
 * the published SOP are untouched, so cancelling the triad leaves the draft saved.
 */
export async function fetchAuthoringDraftRevisionImpact(
  sopId: number,
  options: { enableIfInactive?: boolean } = {},
): Promise<SopAuthoringRevisionImpactPreflight> {
  const query = options.enableIfInactive ? '?enableIfInactive=true' : '';
  return apiFetch<SopAuthoringRevisionImpactPreflight>(
    `/api/sops/${sopId}/authoring-draft/revision-impact${query}`,
  );
}

/** Human-readable outcome for a publish that carried a revision-impact decision. */
export function describeSopRevisionImpactOutcome(
  impact: PublishAuthoringDraftApiResult['revision_impact'],
): string | null {
  if (!impact) return null;
  const count = impact.affected_attempt_count;
  const reports = `${count} in-progress report${count === 1 ? '' : 's'}`;
  if (impact.policy === 'CONTINUE') {
    return `${reports} continue on the sealed procedure; new attempts use the published version.`;
  }
  if (impact.policy === 'PROMPT') {
    return `${reports} will be prompted to continue or restart.`;
  }
  return `${reports} must restart with the published version.`;
}

export async function publishAuthoringDraft(
  sopId: number,
  body: {
    expectedDraftRevision: number;
    expectedPublishedRevision: number;
    policyVersion: number;
    dispositions: Record<string, Record<string, NarrativeBranchDisposition>>;
    enableIfInactive?: boolean;
    revisionImpact?: SopRevisionImpactDecision | null;
  },
): Promise<PublishAuthoringDraftApiResult> {
  return apiFetch<PublishAuthoringDraftApiResult>(`/api/sops/${sopId}/authoring-draft/publish`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function discardAuthoringDraft(sopId: number): Promise<void> {
  await apiFetch(`/api/sops/${sopId}/authoring-draft`, { method: 'DELETE' });
}
